import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  ClientCampaignHistoryItemDto,
  ClientDetailDto,
  ClientListItemDto,
  ClientsClient,
  CreateClientInput,
  CreateClientResult,
  EditClientInput,
} from '@/api/clients-client';
import { useClientsClient } from '@/api/clients-client-context';

// =============================================================================
// use-clients.ts — Hooks TanStack Query du référentiel CLIENTS ADMIN (S6).
// =============================================================================
// Lecture (liste §2.3) en useQuery ; commandes (création, archivage, réactivation)
// en useMutation (effets de bord serveur). Le client effectif vient du
// ClientsClientProvider (doublable, stub-first).
//
// ZÉRO règle métier ici : l'autorité (RBAC, émission DE.Client*, validité des
// transitions Actif↔Archive, unicité SIREN) est l'API. Les hooks relaient l'intention
// et, après une mutation, invalident la liste pour refléter l'état serveur. L'erreur
// API remonte telle quelle.

const CLIENTS_KEY = ['clients'] as const;

/** Clé de query du référentiel clients (§2.3). */
export const clientsListKey = [...CLIENTS_KEY, 'list'] as const;

/** Clé de query de la fiche détail d'un client (§2.3). DISTINCTE de la liste. */
export const clientDetailKey = (siren: string) => [...CLIENTS_KEY, 'detail', siren] as const;

/** Clé de query de la chronologie des campagnes d'un client (§2.3, DBU l.826). */
export const clientCampaignsKey = (siren: string) => [...CLIENTS_KEY, 'campaigns', siren] as const;

/** GET référentiel clients (§2.3, GLOBAL admin-only). */
export function useClientsList(): UseQueryResult<
  readonly ClientListItemDto[],
  Error
> {
  const client: ClientsClient = useClientsClient();
  return useQuery<readonly ClientListItemDto[], Error>({
    queryKey: clientsListKey,
    queryFn: () => client.listClients(),
  });
}

/** GET fiche détail d'un Client (§2.3). Montée AVEC le volet ; enabled tant qu'un SIREN est sélectionné. */
export function useClientDetail(siren: string | null): UseQueryResult<ClientDetailDto, Error> {
  const client: ClientsClient = useClientsClient();
  return useQuery<ClientDetailDto, Error>({
    queryKey: clientDetailKey(siren ?? ''),
    queryFn: () => client.getClientDetail(siren ?? ''),
    enabled: siren !== null && siren.length > 0,
  });
}

/** GET chronologie des campagnes d'un Client (§2.3, DBU l.826). Même cycle que le volet. */
export function useClientCampaigns(
  siren: string | null,
): UseQueryResult<readonly ClientCampaignHistoryItemDto[], Error> {
  const client: ClientsClient = useClientsClient();
  return useQuery<readonly ClientCampaignHistoryItemDto[], Error>({
    queryKey: clientCampaignsKey(siren ?? ''),
    queryFn: () => client.getClientCampaigns(siren ?? ''),
    enabled: siren !== null && siren.length > 0,
  });
}

/** POST créer un Client (Actif + DE.ClientCreated côté API). */
export function useCreateClient(): UseMutationResult<
  CreateClientResult,
  Error,
  CreateClientInput
> {
  const client: ClientsClient = useClientsClient();
  const qc = useQueryClient();
  return useMutation<CreateClientResult, Error, CreateClientInput>({
    mutationKey: [...CLIENTS_KEY, 'create'],
    mutationFn: (input: CreateClientInput) => client.createClient(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsListKey });
    },
  });
}

/** PATCH éditer un Client (attributs métier + contact référent) — DE.ClientEdited côté API. */
export function useEditClient(): UseMutationResult<void, Error, EditClientInput> {
  const client: ClientsClient = useClientsClient();
  const qc = useQueryClient();
  return useMutation<void, Error, EditClientInput>({
    mutationKey: [...CLIENTS_KEY, 'edit'],
    mutationFn: (input: EditClientInput) => client.editClient(input),
    onSuccess: (_result, input) => {
      // Reflète l'état serveur : liste (attributs de ligne) ET fiche détail (attributs + contact).
      void qc.invalidateQueries({ queryKey: clientsListKey });
      void qc.invalidateQueries({ queryKey: clientDetailKey(input.siren) });
    },
  });
}

/** POST archiver un Client (Actif→Archive, no-DELETE) — DESTRUCTIF. */
export function useArchiveClient(): UseMutationResult<void, Error, string> {
  const client: ClientsClient = useClientsClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationKey: [...CLIENTS_KEY, 'archive'],
    mutationFn: (siren: string) => client.archiveClient(siren),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsListKey });
    },
  });
}

/** POST réactiver un Client archivé (Archive→Actif). */
export function useReactivateClient(): UseMutationResult<void, Error, string> {
  const client: ClientsClient = useClientsClient();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationKey: [...CLIENTS_KEY, 'reactivate'],
    mutationFn: (siren: string) => client.reactivateClient(siren),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsListKey });
    },
  });
}
